/* The person's Dashboard layout: loaded from our DB, edited as a draft while
   customising (Done saves it, Cancel restores the snapshot), Gross/Net saved the
   moment it is flipped. Never customised = the default layout for their access,
   which keeps following the newest default until the first Done. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { DashboardLayoutDoc } from '../api/client';
import type { MoneyMode } from '../components/Statistics/unified/model';
import { boardFromDoc, boardToDoc, defaultWidgets, type Access, type Board, type Widget } from './model';

export interface BoardState {
  /** False until the saved layout has been read (or failed to). */
  ready: boolean;
  /** The saved layout could not be read: the default shows and editing waits for a retry. */
  loadFailed: boolean;
  reload: () => void;
  money: MoneyMode;
  /** The widgets on screen: the draft while customising, else the saved ones. */
  widgets: Widget[];
  editing: boolean;
  dirty: boolean;
  saving: boolean;
  startEdit: () => void;
  cancelEdit: () => void;
  /** Resolves false when the save failed (the draft stays open). */
  doneEdit: () => Promise<boolean>;
  setDraft: (update: (widgets: Widget[]) => Widget[]) => void;
  resetToDefault: () => void;
  setMoney: (money: MoneyMode) => Promise<boolean>;
}

export function useBoard(access: Access | null): BoardState {
  const [doc, setDoc] = useState<DashboardLayoutDoc | null>(null);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraftState] = useState<Widget[] | null>(null);
  const [saving, setSaving] = useState(false);
  const snapshot = useRef<Widget[] | null>(null);

  const load = useCallback(() => {
    let live = true;
    setLoadFailed(false);
    api.getDashboardLayout()
      .then((d) => { if (live) setDoc(d); })
      .catch(() => { if (live) setLoadFailed(true); })
      .finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);
  useEffect(() => load(), [load]);

  const stats = access?.stats ?? false;
  const crm = access?.crm ?? false;
  // Rebuilt only when the stored layout or Stats access changes, so default
  // widgets keep their ids between renders.
  const saved: Board = useMemo(() => boardFromDoc(doc, { stats, crm: false }), [doc, stats]);

  const editing = draft !== null;

  const startEdit = useCallback(() => {
    snapshot.current = saved.widgets;
    setDraftState((d) => d ?? saved.widgets);
  }, [saved.widgets]);

  const cancelEdit = useCallback(() => {
    setDraftState(null);
    snapshot.current = null;
  }, []);

  const setDraft = useCallback((update: (widgets: Widget[]) => Widget[]) => {
    setDraftState((d) => update(d ?? saved.widgets));
  }, [saved.widgets]);

  const save = useCallback(async (next: Board) => {
    const out = await api.saveDashboardLayout(boardToDoc(next));
    setDoc(out);
  }, []);

  const doneEdit = useCallback(async () => {
    if (!draft) return true;
    setSaving(true);
    try {
      await save({ money: saved.money, widgets: draft, isDefault: false });
      setDraftState(null);
      snapshot.current = null;
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [draft, save, saved.money]);

  const resetToDefault = useCallback(() => setDraftState(defaultWidgets({ stats, crm })), [stats, crm]);

  const setMoney = useCallback(async (money: MoneyMode) => {
    const prev = doc;
    // Show the flip at once; the default layout stays the default (no widgets saved).
    setDoc({ ...(doc ?? { v: 1 }), ...boardToDoc({ ...saved, money }) });
    try {
      await save({ ...saved, money });
      return true;
    } catch {
      setDoc(prev);
      return false;
    }
  }, [doc, save, saved]);

  const widgets = draft ?? saved.widgets;
  const dirty = editing && snapshot.current !== draft;

  return { ready, loadFailed, reload: load, money: saved.money, widgets, editing, dirty, saving, startEdit, cancelEdit, doneEdit, setDraft, resetToDefault, setMoney };
}
